import type {
  AffirmationControle,
  AjoutControle,
  ChangementTraduction,
  ControleNoyau,
  ControleTraduction,
  IndicateurControle,
  LongueurControle,
  NoyauCode,
} from '@rosetta/shared';

/**
 * What the screens draw from the quality control of Tahiti (#10): pure functions, tested on
 * their own. Nothing here words anything: the sentences are Tahiti's, these functions only pick
 * them out and find their passages in the translated text.
 */

/** The numbering of Tahiti of the indicators the "Texte traduit" tab gives a card to. */
export const INDICATEURS_AFFICHES = {
  affirmations: '2.2',
  ajouts: '2.3',
  longueur: '3.1',
  miseEnForme: '3.2',
} as const;

/** The statuses that make an indicator a point to look at, in the summary of the control. */
const A_REGARDER: ReadonlySet<string> = new Set(['bloquant', 'a_relire', 'erreur']);

/** The status of Tahiti that blocks: the result card says so too (revue P1-4). */
export const STATUT_BLOQUANT = 'bloquant';

/**
 * The status "Information" (#30): Tahiti uses it for the indicator 1.5 "Ton" when the tone of the
 * translation differs from the one of the text without being a fault (read on 8 controls of devdb,
 * 2026-10-13, always on 1.5, `compte_statuts.information` at 1); the counter that says it leads to
 * the card of that indicator.
 */
export const STATUT_INFORMATION = 'information';

/**
 * The statuses whose counter is always there, even at zero or with nothing to lead to (#25): any
 * other status of `compte_statuts` ("Information", "erreur", "sans_objet"...) is shown only when
 * it counts something AND a point of it has a target on the screen (#30, decision of 2026-10-13):
 * if there is nowhere to lead, it is not mentioned at all.
 */
export const STATUTS_TOUJOURS_AFFICHES: ReadonlySet<string> = new Set([
  STATUT_BLOQUANT,
  'a_relire',
  'ok',
]);

/** The control of one noyau, when Tahiti controlled it. */
export function controleDuNoyau(
  controle: ControleTraduction | null | undefined,
  noyau: NoyauCode,
): ControleNoyau | undefined {
  return controle?.noyaux.find((entree) => entree.noyau === noyau);
}

/**
 * What the control gives a screen for the noyau it shows (#34): its entry, or why there is none:
 * `chargement` while the read is under way, `illisible` when it failed (the whole read, or the
 * launch that translated this noyau: Tahiti may well have a control), `indisponible` when Tahiti
 * has none for it (no control, an old plugin, an interrupted launch).
 */
export type LectureControle = ControleNoyau | 'chargement' | 'indisponible' | 'illisible';

/** The part of a query the choice of a `LectureControle` reads. */
export interface EtatLectureControle {
  data: ControleTraduction | null | undefined;
  isPending: boolean;
  isError: boolean;
}

/** The reading of the control for one noyau, from the state of the query. */
export function lectureDuNoyau(etat: EtatLectureControle, noyau: NoyauCode): LectureControle {
  const entree = controleDuNoyau(etat.data, noyau);
  if (entree !== undefined) return entree;
  if (etat.isPending) return 'chargement';
  if (etat.data === undefined ? etat.isError : (etat.data?.noyauxNonLus ?? []).includes(noyau)) {
    return 'illisible';
  }
  return 'indisponible';
}

/** The indicator of this number, when Tahiti gave it in a form that can be read. */
export function indicateur(
  controle: ControleNoyau,
  numero: string,
): IndicateurControle | undefined {
  return controle.indicateurs.find((candidat) => candidat.numero === numero);
}

/** The indicators to look at ("Bloquant", "À relire", in error), in the order of Tahiti. */
export function aRegarder(controle: ControleNoyau): IndicateurControle[] {
  return controle.indicateurs.filter((candidat) => A_REGARDER.has(candidat.statut.code));
}

/**
 * The passages of the translated text the "Bloquant" points are about, without duplicates. The
 * indicators named in `sauf` are left out: the additions (2.3) are marked as additions, not as a
 * passage the control blocks on (#23; in "Avant / après" and in "Texte traduit" alike).
 */
export function passagesBloquants(controle: ControleNoyau, sauf: readonly string[] = []): string[] {
  const passages = controle.indicateurs
    .filter(
      (candidat) => candidat.statut.code === STATUT_BLOQUANT && !sauf.includes(candidat.numero),
    )
    .flatMap((bloquant) => bloquant.points.flatMap((point) => point.passage ?? []));
  return [...new Set(passages)];
}

/**
 * Every affirmation of the text of origin is "presente": then, and only then, none was lost or
 * altered. TO VALIDATE(#10): only "presente" and "affaiblie" were seen (controls 910747 and
 * 910780); the screen says nothing more when another state is there.
 */
export function toutesPresentes(affirmations: readonly AffirmationControle[]): boolean {
  return (
    affirmations.length > 0 &&
    affirmations.every((affirmation) => affirmation.etat.code === 'presente')
  );
}

/** A passage of Tahiti found in the translated text, with what it is about. */
interface Trouve<T> {
  debut: number;
  fin: number;
  element: T;
}

/**
 * Finds the passages of the elements in a text, exactly as Tahiti quotes them (nothing is
 * guessed), every time they are written. An element without a passage, or whose passage is not
 * in the text, is not found. When two overlap, the longer wins; two quoting the same words: the
 * first one of Tahiti. Sorted by position.
 */
function trouver<T>(
  texte: string,
  elements: readonly T[],
  passageDe: (element: T) => string | undefined,
): Trouve<T>[] {
  const trouves: Trouve<T>[] = [];
  for (const element of elements) {
    const passage = passageDe(element);
    if (passage === undefined || passage === '') continue;
    let depart = texte.indexOf(passage);
    while (depart !== -1) {
      trouves.push({ debut: depart, fin: depart + passage.length, element });
      depart = texte.indexOf(passage, depart + 1);
    }
  }
  // A stable sort: on a tie, the element listed first stays first.
  trouves.sort((a, b) => b.fin - b.debut - (a.fin - a.debut) || a.debut - b.debut);
  const retenus: Trouve<T>[] = [];
  for (const candidat of trouves) {
    if (!retenus.some((r) => candidat.debut < r.fin && r.debut < candidat.fin)) {
      retenus.push(candidat);
    }
  }
  return retenus.sort((a, b) => a.debut - b.debut);
}

/** An addition of the control (2.3) found in the translated text, where Tahiti quotes it. */
export interface AjoutRepere {
  debut: number;
  fin: number;
  ajout: AjoutControle;
}

/** Where the additions of the control are written in the translated text (see `trouver`). */
export function reperer(texte: string, ajouts: readonly AjoutControle[]): AjoutRepere[] {
  return trouver(texte, ajouts, (ajout) => ajout.passage).map(({ debut, fin, element }) => ({
    debut,
    fin,
    ajout: element,
  }));
}

/** A passage a "Bloquant" point is about (not an addition) found in the translated text. */
export interface PassageRepere {
  debut: number;
  fin: number;
  passage: string;
}

/**
 * Where the passages the control blocks on (see `passagesBloquants`) are written in the translated
 * text, as Tahiti quotes them, every time they are written (see `trouver`).
 */
export function repererPassages(texte: string, passages: readonly string[]): PassageRepere[] {
  return trouver(texte, passages, (passage) => passage).map(({ debut, fin, element }) => ({
    debut,
    fin,
    passage: element,
  }));
}

/** A change declared by the translation (#28) found in the translated text. */
export interface ChangementRepere {
  debut: number;
  fin: number;
  changement: ChangementTraduction;
}

/**
 * Where the changes declared by the translation are written in the translated text: the
 * `passage` of each one, as Tahiti quotes it, every time it is written (see `trouver`). One
 * that is not found in the text is left out: nothing is invented.
 */
export function repererChangements(
  texte: string,
  changements: readonly ChangementTraduction[],
): ChangementRepere[] {
  return trouver(texte, changements, (changement) => changement.passage).map(
    ({ debut, fin, element }) => ({ debut, fin, changement: element }),
  );
}

/**
 * The sentence of the "Longueur" card (3.1): "36 mots pour 30 à 34 mots visés (+6 %)". Built from
 * the numbers of Tahiti, not copied from its `resume`. The gap is left out when the range is met
 * (`statut` "ok") or when Tahiti gave none.
 */
export function phraseLongueur(longueur: LongueurControle, statut: string): string {
  const { nombre, min, max, unite, ecartPct } = longueur;
  const mot = (n: number): string => (n < 2 && unite.endsWith('s') ? unite.slice(0, -1) : unite);
  const n = (valeur: number): string => valeur.toLocaleString('fr-FR');
  const vise = `${mot(max)}${max < 2 ? ' visé' : ' visés'}`;
  const ecart =
    statut === 'ok' || ecartPct === undefined || ecartPct === 0
      ? ''
      : ` (${ecartPct > 0 ? '+' : '-'}${n(Math.abs(ecartPct))} %)`;
  return `${n(nombre)} ${mot(nombre)} pour ${n(min)} à ${n(max)} ${vise}${ecart}`;
}

/** An indicator of the control the summary can lead to: its number and its name by Tahiti. */
export interface PointListe {
  numero: string;
  nom: string;
}

/**
 * The points of one status ("Bloquant", "À relire", "OK"...) the counter of that status lists:
 * the indicators of Tahiti with this status that HAVE A TARGET in "Texte traduit" (a card: the
 * additions have theirs in the "Contenu" theme since #30), in the order of Tahiti. An indicator
 * without a target is not listed: nothing is invented to point at.
 */
export function pointsDuStatut(
  controle: ControleNoyau,
  statut: string,
  avecCarte: ReadonlySet<string>,
): PointListe[] {
  return controle.indicateurs
    .filter((candidat) => candidat.statut.code === statut && avecCarte.has(candidat.numero))
    .map(({ numero, nom }) => ({ numero, nom }));
}
